<script setup lang="ts">
import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'

import { getAxios } from '../../api/axios'
import { useConfigStore } from '../../stores/config'
import { useAdaMoltenBundle } from '../../composables/use-ada-molten-bundle'
import { useDeviceDetection } from '../../composables/use-device-detection'

import type { PollParagraph } from '../../utils/paragraph-schema/paragraphs'
import type { Brand } from '@xcel-next/core/enums'

const props = withDefaults(defineProps<{
  poll: PollParagraph['poll']
  brand?: Brand
  listOrderPosition?: number
}>(), {
  listOrderPosition: 0
})

const { rsConfig } = storeToRefs(useConfigStore())
const { deviceType } = useDeviceDetection()
const registerSlots = ref<string[]>([])
const { adaSlotReloadOnInteraction } = useAdaMoltenBundle({ registerSlots, deviceType })

const voted = ref(false)
const picked = ref(-1)
const votes = ref<number[]>([1, 2, 3, 4, 5])
const percentages = ref<number[]>([])

const reloadAdsOnInteraction = computed(() => rsConfig.value.adSlotUsage?.reloadAdsOnInteraction)

const pollClasses = computed(() => ({
  'bx-content': true,
  'bx-content__continuous-text': true,
  'bx-typo--content-continuous-text': true,
  'bx-poll': true,
  'bx-poll--voted': voted.value,
  'bx-typo--poll': true
}))

const calcPercentages = (newVotes: number[]) => {
  const sumVotes = newVotes.reduce((acc, val) => acc + val, 0)
  percentages.value = newVotes.map(val => Math.round(100 / sumVotes * val))
}

const submit = async () => {
  try {
    const axios = await getAxios()
    const response = await axios.post<number[]>(
      `${rsConfig.value.hotOrNotServiceUrl}/poll/${props.brand}/${props.poll.reference?.assetId}`,
      { vote: picked.value })
    votes.value = response.data
    calcPercentages(votes.value)
    voted.value = true
    if (reloadAdsOnInteraction.value?.enabled) {
      adaSlotReloadOnInteraction(reloadAdsOnInteraction.value.config)
    }
  } catch (e) {
    console.log('error posting poll: ', e)
  }
}
</script>

<template v-if="poll && brand">
  <div :class="pollClasses">
    <div class="bx-poll__question">
      {{ poll.question }}
    </div>
    <form class="bx-form bx-typo--form"
          onsubmit="return false;">
      <ul>
        <li v-for="(item, index) in poll.answers"
            :key="`poll-${listOrderPosition}-a${index}`">
          <input v-if="!voted"
                 :id="`poll-${listOrderPosition}-a${index}`"
                 v-model="picked"
                 :name="poll.question"
                 :value="index"
                 type="radio">
          <div v-else>
            <progress max="100"
                      :value="percentages[index]">
              {{ percentages[index] }}
            </progress>
            <span>
              <strong>{{ percentages[index] }}%</strong>
            </span>
          </div>
          <label :for="`poll-${listOrderPosition}-a${index}`">
            {{ item.answer }}
            <span v-if="voted">{{ votes[index] }}</span>
          </label>
        </li>
      </ul>
      <button v-if="!voted"
              :disabled="picked < 0"
              class="bx-form__button bx-poll__button"
              @click="submit">
        Abstimmen
      </button>
    </form>
  </div>
</template>
